/**
 * Contentful access for the blog views.
 *
 * The space and environment used to be duplicated as constants at the top of
 * both Blog.vue and BlogPost.vue, along with two near-identical `get()`
 * methods. They're here now so there's one place to change when the space
 * moves, and so the server (server/services/contentful.js) has an obvious
 * counterpart to stay in step with.
 */

const SPACE_ID = process.env.VUE_APP_CONTENTFUL_SPACE_ID || 'oe89s2s3rmwc'
const ENVIRONMENT_ID = 'master'

// `cdn` or `preview` — which Contentful API to read from, per build mode.
const API_HOST = process.env.VUE_APP_CONTENTFUL_ENVIRONMENT

/**
 * @param {object} [params] Extra query parameters.
 * @returns {Promise<object>} The raw Contentful entries envelope.
 */
export async function getEntries(params = {}) {
    const query = new URLSearchParams({
        access_token: process.env.VUE_APP_CONTENTFUL_ACCESS_TOKEN,
        content_type: 'blogPost',
        include: 2,
        ...params,
    })

    const response = await fetch(
        `https://${API_HOST}.contentful.com/spaces/${SPACE_ID}/environments/${ENVIRONMENT_ID}/entries?${query}`
    )
    return response.json()
}

/**
 * Looks a post up the way the server route does: by slug, falling back to the
 * entry id so links shared before the slug field existed still resolve.
 *
 * @param {string} slugOrId
 * @returns {Promise<{item: object|null, includes: object}>}
 */
export async function getPost(slugOrId) {
    const bySlug = await getEntries({ 'fields.slug': slugOrId, limit: 1 })
    if (bySlug.items && bySlug.items.length) {
        return { item: bySlug.items[0], includes: bySlug.includes || {} }
    }

    const byId = await getEntries({ 'sys.id': slugOrId, limit: 1 })
    return {
        item: byId.items && byId.items.length ? byId.items[0] : null,
        includes: byId.includes || {},
    }
}

/**
 * The canonical path for a post. Falls back to the entry id for posts that
 * predate the slug field, which is ugly but never broken.
 *
 * @param {object} post A raw Contentful entry.
 * @returns {string}
 */
export function postPath(post) {
    return `/post/${(post.fields && post.fields.slug) || post.sys.id}`
}

/**
 * A deterministic-but-drifting slice of `posts`, mirroring
 * server/services/blogHtml.js#pickRotating so the client-rendered "more
 * posts" rail behaves the same way as the one crawlers see.
 *
 * A plain `Math.random()` shuffle has no coverage guarantee — with a big pool
 * and a small rail, a post can go a very long time without ever being picked
 * by chance. This instead slides a fixed-size window through the whole list,
 * advancing by one post per hour, so every post surfaces on a predictable
 * cadence instead of maybe.
 *
 * @param {object[]} posts
 * @param {number} [count]
 * @param {number} [now] Injectable for tests.
 * @returns {object[]}
 */
export function pickRotating(posts, count = 8, now = Date.now()) {
    if (posts.length <= count) return posts

    const hourBucket = Math.floor(now / (1000 * 60 * 60))
    const start = hourBucket % posts.length
    return Array.from({ length: count }, (_, i) => posts[(start + i) % posts.length])
}
